import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useTheme
} from "@mui/material";
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";

const RESET_LINK_SENT_MESSAGE =
  "If an account exists for this email, a reset link will be sent.";

const outlinedInputFocusSx = (theme) => ({
  borderRadius: "8px",
  background: "#fafafa",
  fontSize: "14px",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  backgroundColor: theme.palette.background.paper,
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main
  }
});

const ForgotPasswordPage = () => {
  const { forgotPassword } = useContext(AuthContext);
  const [statusMsg, setStatusMsg] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const emailFromParam = searchParams.get("email") || "";

  const theme = useTheme();

  const formik = useFormik({
    initialValues: { email: emailFromParam },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required")
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setStatusMsg({ type: "", message: "" });

      try {
        const { email } = values;

        await forgotPassword({ email });

        setStatusMsg({
          type: "success",
          message: RESET_LINK_SENT_MESSAGE
        });
      } catch (error) {
        if (error?.response?.status === 404) {
          setStatusMsg({
            type: "success",
            message: RESET_LINK_SENT_MESSAGE
          });
          return;
        }

        setStatusMsg({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to send reset link."
        });
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "90vh",
        px: 2,
        py: 5,
        backgroundColor: "#ffffff"
      }}
    >
      {statusMsg.message && (
        <Alert
          severity={statusMsg.type}
          sx={{ mb: 2, borderRadius: 1 }}
          variant='standard'
        >
          {statusMsg.message}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff"
          }}
        >
          <EmailIcon sx={{ fontSize: 32 }} />
        </Box>
      </Box>

      <Typography
        component='h1'
        variant='h5'
        sx={{
          mb: 1,
          color: "#1a1a1a",
          fontWeight: 600,
          fontSize: "24px",
          letterSpacing: "-0.2px",
          textAlign: "center"
        }}
      >
        Forgot password?
      </Typography>

      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ mb: 3, fontSize: "15px", lineHeight: 1.6, textAlign: "center" }}
      >
        Don't worry, please enter your account email,
        <br />
        we will send you a reset password link.
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          label='Email Address'
          name='email'
          type='email'
          variant='outlined'
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          sx={{ mb: 2 }}
          InputProps={{
            sx: outlinedInputFocusSx(theme)
          }}
          InputLabelProps={{
            sx: { fontSize: "14px", color: "#666" }
          }}
        />

        <Button
          type='submit'
          variant='contained'
          fullWidth
          disabled={loading}
          sx={{
            width: "100%",
            minHeight: 48,
            py: 1.5,
            fontSize: "15px",
            fontWeight: 600,
            boxShadow: "0 2px 6px rgba(0, 102, 255, 0.3)",
            transition: "background 0.2s ease",
            borderRadius: 2
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Send Reset Link"
          )}
        </Button>
      </form>

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Button
          component={Link}
          to='/auth/login'
          startIcon={<ArrowBackIcon sx={{ fontSize: "16px" }} />}
          sx={{
            color: "#666",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 500,
            px: 0,
            minWidth: 0,
            "&:hover": { color: "#0078D4" }
          }}
        >
          Back to Login Page
        </Button>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
